/**
 * ImageViewer Component
 *
 * A sophisticated image viewer component that provides:
 * - Image zooming and panning capabilities
 * - Annotation system for adding comments and markers to images
 * - Responsive image scaling and positioning
 * - Touch and mouse interaction support
 */

import { useEffect, useRef, useState, useCallback } from "react"

import { MinusCircleOutlined, PlusCircleOutlined } from "@ant-design/icons"
import classNames from "classnames"
import { debounce } from "lodash"
import { useDispatch } from "react-redux"
import styled from "styled-components"

import { filesPreviewActions } from "../model/slice/filesPreviewSlice"

import { ANNOTATION_COMMENT_ACTION, AssetAnnotations } from "./AssetAnnotations"
import { calculateAnnotationCoordinates, isSelectedAnnotationHasCoordinates } from './utils/annotationUtils'

interface ImageViewerProps {
  previewUrl: string                    // URL of the image to display
  handleFinishLoading: () => void      // Callback when image finishes loading
  handleError: () => void              // Callback when image fails to load
  isLoading: boolean                   // Loading state of the image
  areControlsVisible: boolean          // Controls visibility state
  annotations: any[]                   // Array of image annotations
  selectedAnnotation: any              // Currently selected annotation
  newAnnotation: any                   // New annotation being created
  onAnnotationSubmit: (annotation: any) => void  // Callback for submitting new annotation
  isCommentsLoading: boolean           // Loading state for comments
  userName: string
  isShowAnnotationLayer: boolean
  isShowAnnotationMarkers: boolean
  isCreatingNewAnnotation: boolean
  creatingNewAnnotationMode: CREATING_NEW_ANNOTATION_MODE | null
  onAnnotationDelete: (annotation: any) => void  // Callback for deleting annotation
  onAnnotationEdit: (annotation: any) => void    // Callback for editing annotation
  onAnnotationResolve: (annotation: any) => void // Callback for resolving annotation
}

// Constants for zoom limits
const MAX_ZOOM = 500  // Maximum zoom level (500%)
const MIN_ZOOM = 100  // Minimum zoom level (100%)
const ZOOM_STEP = 10  // Zoom step (10%)
const STAGE_MARGIN = 0 // Margin for the stage

export const ImageViewer = ({
  previewUrl,
  handleFinishLoading,
  handleError,
  isLoading,
  areControlsVisible,
  annotations,
  onAnnotationSubmit,
  isCommentsLoading,
  userName,
  selectedAnnotation,
  newAnnotation,
  isShowAnnotationLayer,
  isShowAnnotationMarkers,
  isCreatingNewAnnotation,
  creatingNewAnnotationMode,
  onAnnotationDelete,
  onAnnotationEdit,
  onAnnotationResolve,
}: ImageViewerProps) => {
  const dispatch = useDispatch()
  const [currentZoom, setCurrentZoom] = useState(MIN_ZOOM)  // Current zoom level
  const [isDragging, setIsDragging] = useState(false)  // Dragging state for panning
  const [position, setPosition] = useState({ x: 0, y: 0 })  // Current image position
  const [startPosition, setStartPosition] = useState({ x: 0, y: 0 })  // Starting position for drag
  const [assetSize, setAssetSize] = useState({ width: 0, height: 0 })
  const [containerOffset, setContainerOffset] = useState({ x: 0, y: 0 })  // Offset for image container

  const imgRef = useRef<HTMLImageElement>(null)  // Reference to the image element
  const containerRef = useRef<HTMLDivElement>(null)  // Reference to the container element
  const wrapperRef = useRef<HTMLDivElement>(null)  // Reference to the wrapper element
  useEffect(() => {
    if(!selectedAnnotation) dispatch(filesPreviewActions.setReplyingCommentId(null))
  }, [selectedAnnotation])

  const calculateAssetSize = () => {
    let assetWidth = 0
    let assetHeight = 0
    if (!imgRef.current) return { width: assetWidth, height: assetHeight }
    const containerOffset = { x: 0, y: 0 }
    const imgNaturalWidth = imgRef.current.naturalWidth
    const imgNaturalHeight = imgRef.current.naturalHeight
    const aspectRatio = imgNaturalWidth / imgNaturalHeight

    const imgWidth = imgRef.current?.width || 0
    const imgHeight = imgRef.current?.height || 0

    if(imgNaturalWidth > imgNaturalHeight) {
      assetWidth = imgWidth
      assetHeight = imgWidth / aspectRatio
      containerOffset.y = Math.max((imgHeight - assetHeight) / 2, 0)
    } else {
      assetHeight = imgHeight
      assetWidth = imgHeight * aspectRatio
      containerOffset.x = Math.max((imgWidth - assetWidth) / 2, 0)
    }

    return { width: assetWidth, height: assetHeight, containerOffset }
  }

  /**
   * Calculates the base dimensions of the image to fit within the container
   * while maintaining aspect ratio
   */
  const calculateBaseBoundaries = useCallback(({ containerWidth, containerHeight }): {width:number, height: number} => {
    if (!imgRef.current || !containerRef.current) return { width: 0, height: 0 }

    const imgNaturalWidth = imgRef.current.naturalWidth
    const imgNaturalHeight = imgRef.current.naturalHeight

    let baseWidth, baseHeight
    const aspectRatio = imgNaturalWidth / imgNaturalHeight
    const containerAspectRatio = containerWidth / containerHeight

    if (aspectRatio > containerAspectRatio) {
      baseWidth = containerWidth
      baseHeight = containerWidth / aspectRatio
    } else {
      baseHeight = containerHeight
      baseWidth = containerHeight * aspectRatio
    }

    return { width: baseWidth, height: baseHeight }
  }, [])

  /**
   * Calculates the boundaries for image movement based on current zoom level
   * Ensures the image stays within viewable area
   */
  const calculateBoundaries = useCallback((zoom: number) => {
    if (!imgRef.current || !containerRef.current) return { maxX: 0, maxY: 0, minX: 0, minY: 0 }

    const scale = zoom / 100
    const containerWidth = containerRef.current.clientWidth
    const containerHeight = containerRef.current.clientHeight
    const { width: baseWidth, height: baseHeight } = calculateBaseBoundaries({ containerWidth, containerHeight })
    const scaledWidth = baseWidth * scale
    const scaledHeight = baseHeight * scale

    const horizontalOverflow = Math.max(0, scaledWidth - containerWidth)
    const verticalOverflow = Math.max(0, scaledHeight - containerHeight)

    return {
      maxX: horizontalOverflow / 2,
      minX: -horizontalOverflow / 2,
      maxY: verticalOverflow / 2,
      minY: -verticalOverflow / 2
    }
  }, [calculateBaseBoundaries])

  /**
   * Constrains the image position to stay within calculated boundaries
   */
  const constrainPosition = useCallback((pos: { x: number, y: number }, zoom: number) => {
    const { maxX, minX, maxY, minY } = calculateBoundaries(zoom)

    return {
      x: Math.min(Math.max(pos.x, minX), maxX),
      y: Math.min(Math.max(pos.y, minY), maxY)
    }
  }, [calculateBoundaries])

  /**
   * Updates the image container transform based on zoom and position
   * Handles scaling and translation of the image
   */
  const updateImageContainerTransform = useCallback((zoom: number, pos: { x: number, y: number }) => {
    if (containerRef.current) {
      const scale = zoom / 100
      const constrainedPos = constrainPosition(pos, zoom)
      const translateX = constrainedPos.x / scale
      const translateY = constrainedPos.y / scale

      containerRef.current.style.transform = `scale(${scale}) translate3d(${translateX}px, ${translateY}px, 0px)`

      if (zoom > MIN_ZOOM) {
        containerRef.current.style.cursor = isDragging ? 'grabbing' : 'grab'
      } else {
        containerRef.current.style.cursor = 'default'
      }

      return constrainedPos
    }

    return pos
  }, [isDragging, constrainPosition])

  // Effect to update asset boundaries when image loads or annotation mode changes
  useEffect(() => {
    if (isLoading || !imgRef.current || !containerRef.current) return
    const { width: imgWidth, height: imgHeight, containerOffset } = calculateAssetSize()
    setContainerOffset(containerOffset)
    setAssetSize({ width: imgWidth, height: imgHeight })
  }, [isLoading, isCreatingNewAnnotation, isShowAnnotationLayer, isShowAnnotationMarkers, imgRef.current, containerRef.current])

  // Effect to reset annotation state when annotation mode is disabled
  useEffect(() => {
    if(!isCreatingNewAnnotation || !isShowAnnotationLayer) {
      dispatch(filesPreviewActions.setNewAnnotation(null))
    }
  }, [isCreatingNewAnnotation, isShowAnnotationLayer])

  // Effect to reset viewer state when image URL changes
  useEffect(() => {
    return () => {
      setCurrentZoom(MIN_ZOOM)
      setPosition({ x: 0, y: 0 })
      setIsDragging(false)
      setStartPosition({ x: 0, y: 0 })
    }
  }, [previewUrl])

  // Effect to handle zoom changes and position constraints
  useEffect(() => {
    // prevent zooming when annotating
    if(isCreatingNewAnnotation) return
    if (currentZoom <= MIN_ZOOM) {
      const newPosition = { x: 0, y: 0 }
      updateImageContainerTransform(currentZoom, newPosition)
    } else {
      const constrainedPos = constrainPosition(position, currentZoom)
      updateImageContainerTransform(currentZoom, constrainedPos)
    }
  }, [currentZoom, isCreatingNewAnnotation, isShowAnnotationLayer, isShowAnnotationMarkers, position])

  // Effect to handle window resize and update image positioning
  useEffect(() => {

    const handleResize = () => {
      if (currentZoom > MIN_ZOOM) {
        const constrainedPos = constrainPosition(position, currentZoom)
        setPosition(constrainedPos)
        updateImageContainerTransform(currentZoom, constrainedPos)
      }
      if (containerRef.current) {
        containerRef.current.style.transformOrigin = 'center'
        updateImageContainerTransform(currentZoom, position)
        const { width: imgWidth, height: imgHeight, containerOffset } = calculateAssetSize()
        setContainerOffset(containerOffset)
        setAssetSize({ width: imgWidth, height: imgHeight })
      }
    }

    const debouncedHandleResize = debounce(handleResize, 200)
    window.addEventListener('resize', debouncedHandleResize)

    return () => {
      window.removeEventListener('resize', debouncedHandleResize)
      debouncedHandleResize.cancel() // Cancel any pending debounced calls
    }
  }, [currentZoom, position])

  // Effect to handle mouse wheel zoom
  useEffect(() => {
    const containerElement = containerRef.current

    const handleWheelEvent = (e: WheelEvent) => {
      if (e.ctrlKey) {
        const wheelZoomStep = ZOOM_STEP / 2
        e.preventDefault()
        e.stopPropagation()
        const delta = e.deltaY < 0 ? wheelZoomStep : wheelZoomStep * -1

        const newZoom = Math.min(Math.max(currentZoom + delta, MIN_ZOOM), MAX_ZOOM)

        if (newZoom === currentZoom || isCreatingNewAnnotation) return

        setCurrentZoom(newZoom)
        updateImageContainerTransform(newZoom, position)
      }
    }

    if (containerElement) {
      containerElement.addEventListener('wheel', handleWheelEvent, { passive: false })
    }

    return () => {
      if (containerElement) {
        containerElement.removeEventListener('wheel', handleWheelEvent)
      }
    }
  }, [currentZoom, position, isCreatingNewAnnotation])

  /**
   * Handlers for zoom controls
   */
  const handleZoomIn = () => {
    if (currentZoom >= MAX_ZOOM) {
      return
    }

    const newZoom = currentZoom + ZOOM_STEP
    setCurrentZoom(newZoom)
    updateImageContainerTransform(newZoom, position)
  }

  const handleZoomOut = () => {
    if (currentZoom <= MIN_ZOOM) {
      return
    }

    const newZoom = currentZoom - ZOOM_STEP
    setCurrentZoom(newZoom)
    updateImageContainerTransform(newZoom, position)
  }

  /**
   * Mouse event handlers for image panning
   */
  const handleMouseDown = (e: React.MouseEvent) => {
    if (currentZoom > MIN_ZOOM && containerRef.current) {
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(true)

      setStartPosition({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      })
      containerRef.current.style.cursor = 'grabbing'
      containerRef.current.style.transition = 'none'
    }
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && currentZoom > MIN_ZOOM && containerRef.current) {
      e.preventDefault()
      e.stopPropagation()

      const newPosition = {
        x: e.clientX - startPosition.x,
        y: e.clientY - startPosition.y
      }

      const constrainedPos = constrainPosition(newPosition, currentZoom)

      const scale = currentZoom / 100
      const translateX = constrainedPos.x / scale
      const translateY = constrainedPos.y / scale
      containerRef.current.style.transform = `scale(${scale}) translate3d(${translateX}px, ${translateY}px, 0px})`

      setPosition(constrainedPos)
    }
  }

  const handleMouseUp = (e: React.MouseEvent) => {
    if (isDragging && containerRef.current && currentZoom > MIN_ZOOM) {
      e.preventDefault()
      e.stopPropagation()

      setIsDragging(false)
      containerRef.current.style.cursor = 'grab'
      containerRef.current.style.transition = 'transform 0.1s ease-out'
    }
  }

  const handleMouseLeave = (e: React.MouseEvent) => {
    if (isDragging && containerRef.current && currentZoom > MIN_ZOOM) {
      e.preventDefault()
      e.stopPropagation()

      setIsDragging(false)
      containerRef.current.style.cursor = 'grab'
      containerRef.current.style.transition = 'transform 0.1s ease-out'
    }
  }

  /**
   * Annotation-related handlers
   */
  const scrollToAnnotation = (annotation: any) => {
    const annotationData = annotation.data
    const commentElement = document.getElementById(`${annotationData.id}`)
    if (commentElement) {
      commentElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  const handleAnnotationSelect = (annotation: any) => {
    if (!annotation) {
      dispatch(filesPreviewActions.setSelectedAnnotation(null))
      return
    }

    dispatch(filesPreviewActions.setReplyingCommentId(null))

    const containerWidth = containerRef.current.clientWidth
    const containerHeight = containerRef.current.clientHeight
    const normalizedAnnotation = {
      ...annotation,
      geometry: {
        absolute: {
          x: annotation.x / 100 * containerWidth,
          y: annotation.y / 100 * containerHeight,
          width: annotation.width / 100 * containerWidth,
          height: annotation.height / 100 * containerHeight
        },
        relative: {
          x: annotation.x,
          y: annotation.y,
          width: annotation.width,
          height: annotation.height
        }
      }
    }

    scrollToAnnotation(normalizedAnnotation)
    dispatch(filesPreviewActions.setSelectedAnnotation(normalizedAnnotation))
    dispatch(filesPreviewActions.setNewAnnotation(null))
  }

  const handleAnnotationSubmit = (annotation: any) => {
    const { width: imgWidth, height: imgHeight } = calculateAssetSize()
    const { coordinates, xPosPercent, yPosPercent } = calculateAnnotationCoordinates(
      annotation,
      imgWidth,
      imgHeight,
      STAGE_MARGIN
    )

    onAnnotationSubmit({ ...annotation, x: xPosPercent, y: yPosPercent, coordinates })
    dispatch(filesPreviewActions.setSelectedAnnotation(null))
    dispatch(filesPreviewActions.setNewAnnotation(null))
  }

  const handleAnnotationDelete = (annotation: any) => {
    onAnnotationDelete(annotation)
  }

  const handleAnnotationEdit = (annotation: any) => {
    onAnnotationEdit(annotation)
    dispatch(filesPreviewActions.setSelectedAnnotation(null))
  }

  const handleAnnotationCommentClose = () => {
    dispatch(filesPreviewActions.setSelectedAnnotation(null))
    dispatch(filesPreviewActions.setNewAnnotation(null))
    dispatch(filesPreviewActions.setIsCreatingNewAnnotation(false))
  }

  const handleCreateNewAnnotation = (annotation: any) => {
    dispatch(filesPreviewActions.setIsCreatingNewAnnotation(true))
    dispatch(filesPreviewActions.setSelectedAnnotation(null))
    dispatch(filesPreviewActions.setNewAnnotation(annotation))
  }

  const handleClearNewAnnotation = () => {
    dispatch(filesPreviewActions.setSelectedAnnotation(null))
    dispatch(filesPreviewActions.setNewAnnotation(null))
  }

  const handleAnnotationCommentAction = (action: ANNOTATION_COMMENT_ACTION, annotation: any ) => {
    switch (action) {
    case ANNOTATION_COMMENT_ACTION.ADD:
      handleAnnotationSubmit(annotation)
      dispatch(filesPreviewActions.setNewAnnotation(null))
      return
    case ANNOTATION_COMMENT_ACTION.EDIT:
      handleAnnotationEdit(annotation)
      dispatch(filesPreviewActions.setSelectedAnnotation(null))
      return
    case ANNOTATION_COMMENT_ACTION.DELETE:
      handleAnnotationDelete(annotation)
      dispatch(filesPreviewActions.setSelectedAnnotation(null))
      return
    case ANNOTATION_COMMENT_ACTION.OPEN_REPLY:
      dispatch(filesPreviewActions.setSelectedAnnotation(annotation))
      return
    case ANNOTATION_COMMENT_ACTION.RESOLVE:
      onAnnotationResolve(annotation)
      dispatch(filesPreviewActions.setSelectedAnnotation(null))
      return
    }
  }

  const handleReplyToComment = (annotation: any) => {
    dispatch(filesPreviewActions.setReplyingCommentId(annotation.data.id))
  }

  const handleCanvasClickOutside = () => {
    if (selectedAnnotation) {
      dispatch(filesPreviewActions.setSelectedAnnotation(null))
    }
  }

  const isCommentVisible = newAnnotation || isSelectedAnnotationHasCoordinates(selectedAnnotation)
  const boundingRect = wrapperRef.current?.getBoundingClientRect()

  return (
    <SImageWrapper
      ref={wrapperRef}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleMouseDown}
    >
      <SImgContainer
        ref={containerRef}
      >
        <SImg
          ref={imgRef}
          src={previewUrl}
          onLoad={handleFinishLoading}
          onError={handleError}
          fetchPriority='high'
          className={classNames({ 'not-loaded': isLoading })}
          draggable={false}
        />

        <AssetAnnotations
          stageMargin={STAGE_MARGIN}
          className="asset-annotations"
          parentRef={containerRef}
          assetRef={imgRef}
          assetBoundingBox={assetSize}
          isCanvasVisible={isShowAnnotationLayer}
          isCommentVisible={isCommentVisible}
          currentZoom={currentZoom}
          position={position}
          isDragging={isDragging}
          previewUrl={previewUrl}
          annotations={annotations}
          newAnnotation={newAnnotation}
          selectedAnnotation={selectedAnnotation}
          creatingNewAnnotationMode={creatingNewAnnotationMode}
          onAnnotationSelect={handleAnnotationSelect}
          onAnnotationSubmit={handleAnnotationSubmit}
          onCreateNewAnnotation={handleCreateNewAnnotation}
          onClearNewAnnotation={handleClearNewAnnotation}
          showMarkers={isShowAnnotationMarkers}
          onCommentClose={handleAnnotationCommentClose}
          onCommentAction={handleAnnotationCommentAction}
          onReplyToComment={handleReplyToComment}
          boundingRect={boundingRect}
          containerOffset={containerOffset}
          onCanvasClickOutside={handleCanvasClickOutside}
        />

      </SImgContainer>

      {!isCreatingNewAnnotation && (
        <SBottomControls className={classNames({ hidden: !areControlsVisible })}>
          <SZoomWrapper>
            <SZoomOutButton onClick={handleZoomOut} />

            <SCurrentZoom>
              {currentZoom}
%
            </SCurrentZoom>

            <SZoomInButton onClick={handleZoomIn} />
          </SZoomWrapper>
        </SBottomControls>
      )}
    </SImageWrapper>
  )
}

const SImageWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
`

const SImgContainer = styled.div`
  position: relative;
  height: calc(100% - ${STAGE_MARGIN}px);
  display: flex;
  justify-content: center;
  align-items: center;
`

const SImg = styled.img`
  height: 100%;
  width: 100%;
  display: block;
  margin: 0;
  user-select: none;
  object-fit: contain;
  transition: transform 0.3s ease-out;

  &.not-loaded {
    display: none;
  }
`

const SBottomControls = styled.div`
  position: absolute;
  bottom: 20px;
  left: calc(50% - 30px);
  display: flex;
  justify-content: center;
  opacity: 1;
  transition: opacity 0.3s;

  &.hidden {
    opacity: 0;
  }
`

const SZoomWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  gap: 16px;
`

const SZoomInButton = styled(PlusCircleOutlined)`
  color: #fff;
  font-size: 24px;
  border: none;
  cursor: pointer;
`

const SZoomOutButton = styled(MinusCircleOutlined)`
  color: #fff;
  font-size: 24px;
  border: none;
  cursor: pointer;
`

const SCurrentZoom = styled.div`
  color: #fff;
`
